import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, Pencil, Plus, Search, Shield, Trash2, UserCheck, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError, type AdminAccount, type AdminUser } from "../../lib/api";
import { canAccess } from "../../lib/auth-tokens";
import { ConfirmDialog, ModalShell } from "../../components/admin/AdminModals";
import { AdminEmptyState, AdminPageHeader } from "../../components/admin/AdminUI";

export function AdminAdmins() {
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? null;
  const allowed = canAccess(tokens, "admins.manage");

  const [items, setItems] = useState<AdminAccount[]>([]);
  const [catalog, setCatalog] = useState<{ key: string; label: string }[]>([]);
  const [defaults, setDefaults] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successBanner, setSuccessBanner] = useState("");
  const [edit, setEdit] = useState<AdminAccount | null>(null);
  const [creating, setCreating] = useState(false);
  const [addMode, setAddMode] = useState<"select" | "manual">("select");
  const [userSearch, setUserSearch] = useState("");
  const [userSearching, setUserSearching] = useState(false);
  const [userResults, setUserResults] = useState<AdminUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  const [deleteId, setDeleteId] = useState<AdminAccount | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AdminAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    password: "",
    is_super_admin: false,
    is_active: true,
    permissions: [] as string[],
  });

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    api.admin.listAdmins(token)
      .then((d) => {
        setItems(d.items);
        setCatalog(d.featureCatalog.filter((f) => f.key !== "admins.manage"));
        setDefaults(d.defaultPermissions);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load admins"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Search users to select for promotion
  useEffect(() => {
    if (!creating || addMode !== "select" || !token) return;
    let active = true;
    setUserSearching(true);
    const timer = setTimeout(() => {
      api.admin.users(token, { search: userSearch.trim(), status: "active" })
        .then((res) => {
          if (!active) return;
          const adminPhones = new Set(items.map((a) => a.phone));
          const available = res.items.filter((u) => !adminPhones.has(u.phone));
          setUserResults(available);
        })
        .catch(() => {
          if (active) setUserResults([]);
        })
        .finally(() => {
          if (active) setUserSearching(false);
        });
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [creating, addMode, userSearch, token, items]);

  function openCreate() {
    setForm({
      full_name: "",
      phone: "",
      password: "",
      is_super_admin: false,
      is_active: true,
      permissions: [...defaults],
    });
    setAddMode("select");
    setUserSearch("");
    setSelectedUser(null);
    setUserResults([]);
    setFormError("");
    setCreating(true);
  }

  function openEdit(a: AdminAccount) {
    setForm({
      full_name: a.fullName,
      phone: a.phone,
      password: "",
      is_super_admin: a.isSuperAdmin,
      is_active: a.isActive,
      permissions: a.permissions.filter((p) => p !== "admins.manage"),
    });
    setFormError("");
    setEdit(a);
  }

  function togglePerm(key: string) {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key],
    }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setFormError("");
    try {
      if (creating) {
        if (addMode === "select") {
          if (!selectedUser) {
            setFormError("Please select a registered user to promote");
            setBusy(false);
            return;
          }
          const res = await api.admin.promoteUser(token, selectedUser.id, {
            is_super_admin: form.is_super_admin,
            permissions: form.is_super_admin ? undefined : form.permissions,
            password: form.password.trim() || undefined,
          });
          setSuccessBanner(
            `Successfully promoted ${selectedUser.name} to administrator.${
              res.pendingSetup
                ? " They can set their password via the Telegram bot or web setup link."
                : ""
            }`
          );
          setCreating(false);
        } else {
          await api.admin.createAdmin(token, {
            phone: form.phone.trim(),
            full_name: form.full_name.trim(),
            password: form.password,
            is_super_admin: form.is_super_admin,
            permissions: form.is_super_admin ? undefined : form.permissions,
          });
          setSuccessBanner(`Created administrator account for ${form.full_name.trim()}.`);
          setCreating(false);
        }
      } else if (edit) {
        await api.admin.updateAdmin(token, edit.id, {
          full_name: form.full_name.trim(),
          is_active: form.is_active,
          is_super_admin: form.is_super_admin,
          permissions: form.is_super_admin ? undefined : form.permissions,
          password: form.password || undefined,
        });
        setSuccessBanner(`Updated administrator account for ${form.full_name.trim()}.`);
        setEdit(null);
      }
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!token || !deleteId) return;
    setBusy(true);
    try {
      await api.admin.deleteAdmin(token, deleteId.id);
      setDeleteId(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not deactivate admin");
      setDeleteId(null);
    } finally {
      setBusy(false);
    }
  }

  async function confirmRevoke() {
    if (!token || !revokeTarget) return;
    setBusy(true);
    try {
      await api.admin.revokeAdmin(token, revokeTarget.id, {
        reason: "Administrator privileges revoked by super administrator.",
      });
      setRevokeTarget(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not revoke administrator privileges");
      setRevokeTarget(null);
    } finally {
      setBusy(false);
    }
  }

  if (!allowed) {
    return (
      <div className="p-8">
        <p className="text-sm text-warning bg-warning/10 border border-warning/30 rounded-lg px-4 py-3">
          Only super admins can manage administrator accounts and permissions.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20 text-muted-foreground">
        <Loader2 className="animate-spin mr-2" /> Loading admins…
      </div>
    );
  }

  const modalOpen = creating || !!edit;

  return (
    <div className="p-8 space-y-6">
      <AdminPageHeader
        title="Administrators"
        subtitle="Super admin only — assign feature access for each admin"
        actions={
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-on-accent text-sm font-semibold rounded-lg shadow-xs hover:opacity-90 transition">
            <Plus size={15} /> Add / Promote Admin
          </button>
        }
      />

      {successBanner && (
        <div className="flex items-center justify-between text-sm text-success bg-success/10 border border-success/30 rounded-lg px-4 py-3">
          <span>{successBanner}</span>
          <button type="button" onClick={() => setSuccessBanner("")} className="font-bold text-success hover:underline">
            <X size={15} />
          </button>
        </div>
      )}

      {error && <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">{error}</p>}

      <div className="bg-card rounded-lg border border-border overflow-hidden shadow-xs">
        <div className="overflow-x-auto admin-scroll-x">
          <table className="w-full text-sm">
            <thead>
            <tr className="border-b border-border bg-muted/50">
              {["Name", "Phone", "Role", "Features", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 ? (
              <tr><td colSpan={6}><AdminEmptyState message="No admins" /></td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="hover:bg-muted/40">
                <td className="px-4 py-3 font-semibold flex items-center gap-2">
                  {a.isSuperAdmin && <Shield size={14} className="text-warning" />}
                  {a.fullName}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{a.phone}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-start">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.isSuperAdmin ? "bg-warning/10 text-warning" : "bg-secondary text-primary"}`}>
                      {a.isSuperAdmin ? "Super admin" : "Admin"}
                    </span>
                    {a.pendingSetup && (
                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-warning/10 text-warning border border-warning/30">
                        Setup pending
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {a.isSuperAdmin ? "All features" : `${a.permissions.length} assigned`}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold ${a.isActive ? "text-success" : "text-destructive"}`}>
                    {a.isActive ? "Active" : "Suspended"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 justify-end items-center">
                    <button type="button" onClick={() => openEdit(a)} title="Edit permissions" className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground">
                      <Pencil size={14} />
                    </button>
                    {!a.isSuperAdmin && (
                      <>
                        <button
                          type="button"
                          onClick={() => setRevokeTarget(a)}
                          title="Revoke Administrator Privileges"
                          className="px-2 py-1 rounded-md text-xs font-medium text-destructive hover:bg-destructive/10 border border-destructive/30 transition"
                        >
                          Revoke
                        </button>
                        <button type="button" onClick={() => setDeleteId(a)} title="Deactivate account" className="w-8 h-8 rounded-lg hover:bg-destructive/10 text-destructive hover:text-destructive flex items-center justify-center">
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      </div>

      <ModalShell
        open={modalOpen}
        onClose={() => { setCreating(false); setEdit(null); }}
        title={creating ? "Add / Promote Administrator" : "Edit Administrator"}
        subtitle={creating ? "Select an existing student to promote or register a new administrator account" : "Choose which admin panel features this account can use"}
        wide
      >
        <form onSubmit={save} className="space-y-4">
          {formError && <p className="text-sm text-destructive">{formError}</p>}

          {creating && (
            <div className="flex rounded-lg border border-border p-1 bg-muted/40">
              <button
                type="button"
                onClick={() => setAddMode("select")}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition ${
                  addMode === "select" ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Select from Registered Users
              </button>
              <button
                type="button"
                onClick={() => setAddMode("manual")}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition ${
                  addMode === "manual" ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Manual New Account
              </button>
            </div>
          )}

          {creating && addMode === "select" ? (
            <div className="space-y-3">
              {selectedUser ? (
                <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 text-primary font-bold flex items-center justify-center text-sm">
                      {selectedUser.name?.slice(0, 2).toUpperCase() || "ST"}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                        {selectedUser.name}
                        <Check size={14} className="text-primary" />
                      </p>
                      <p className="text-xs text-muted-foreground">{selectedUser.phone} {selectedUser.university ? `· ${selectedUser.university}` : ""}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedUser(null)}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                  >
                    Change user
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
                    <input
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search student by name or phone (+251...)"
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-border text-sm bg-card"
                    />
                  </div>

                  <div className="max-h-56 overflow-y-auto admin-scroll-y rounded-lg border border-border divide-y divide-border">
                    {userSearching ? (
                      <div className="p-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                        <Loader2 size={14} className="animate-spin" /> Searching users…
                      </div>
                    ) : userResults.length === 0 ? (
                      <div className="p-4 text-center text-xs text-muted-foreground">
                        {userSearch ? "No registered students found matching your search." : "Type a name or phone to search registered students."}
                      </div>
                    ) : (
                      userResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setSelectedUser(u);
                            setForm((f) => ({ ...f, full_name: u.name, phone: u.phone }));
                            setFormError("");
                          }}
                          className="w-full px-3 py-2.5 text-left flex items-center justify-between hover:bg-muted/50 transition"
                        >
                          <div>
                            <p className="font-medium text-sm text-foreground">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.phone} {u.university ? `· ${u.university}` : ""}</p>
                          </div>
                          <span className="text-xs text-primary font-semibold px-2 py-1 rounded bg-primary/10">
                            Select
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {selectedUser && (
                <div className="pt-2 border-t border-border/60">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Initial Password (optional)
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Leave blank to let them set it via Telegram or web link"
                    className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-0.5">
                    If left blank, the student will be prompted to set their password upon promotion.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" required className="w-full px-3 py-2.5 rounded-lg border border-border text-sm col-span-2 sm:col-span-1" />
              {creating ? (
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (+251…)" required className="w-full px-3 py-2.5 rounded-lg border border-border text-sm col-span-2 sm:col-span-1" />
              ) : (
                <input value={form.phone} disabled className="w-full px-3 py-2.5 rounded-lg border border-border text-sm bg-muted col-span-2 sm:col-span-1" />
              )}
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={creating ? "Password" : "New password (optional)"}
                required={creating && addMode === "manual"}
                className="w-full px-3 py-2.5 rounded-lg border border-border text-sm col-span-2"
              />
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setForm({ ...form, is_super_admin: !form.is_super_admin })}
              className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition ${form.is_super_admin ? "bg-warning/10 border-warning/30 text-warning" : "bg-muted border-border"}`}
            >
              {form.is_super_admin ? "Super admin" : "Regular admin"}
            </button>
            {edit && (
              <button
                type="button"
                onClick={() => setForm({ ...form, is_active: !form.is_active })}
                className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition ${form.is_active ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"}`}
              >
                {form.is_active ? "Active" : "Suspended"}
              </button>
            )}
          </div>

          {!form.is_super_admin && (
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Feature access</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border border-border rounded-xl p-3">
                {catalog.map((f) => (
                  <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.permissions.includes(f.key)}
                      onChange={() => togglePerm(f.key)}
                      className="rounded border-border"
                    />
                    <span>{f.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || (creating && addMode === "select" && !selectedUser)}
            className="w-full py-2.5 bg-primary text-on-accent rounded-lg font-semibold text-sm disabled:opacity-60 transition"
          >
            {busy
              ? "Saving…"
              : creating && addMode === "select" && selectedUser
              ? `Promote ${selectedUser.name} to Admin`
              : creating
              ? "Create Admin Account"
              : "Save Changes"}
          </button>
        </form>
      </ModalShell>

      <ConfirmDialog
        open={!!deleteId}
        title="Deactivate admin?"
        message={`Suspend ${deleteId?.fullName}? They will lose access to the admin panel.`}
        confirmLabel="Deactivate"
        variant="destructive"
        loading={busy}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke Administrator Privileges?"
        message={`Revoke administrator access for ${revokeTarget?.fullName}? All active admin sessions and refresh tokens will be immediately invalidated. Their student account, courses, and coursework will remain fully preserved.`}
        confirmLabel="Revoke Privileges"
        variant="destructive"
        loading={busy}
        onClose={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
      />
    </div>
  );
}
